import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';

import { Board } from 'types';
import { State } from 'redux/initialState';

import { getBoards } from 'modules/boards/redux/boardActions';
import { getPartnerRepresentants } from 'modules/partners/redux/partnerRepresentantsActions';
import { isBoardVisibleAtList } from 'modules/boards/utils';

export const useBoardsPartnerList = (
  societyId: string | undefined,
  memberId: string | undefined,
  user: any
) => {
  const dispatch = useDispatch();

  const boards: Board[] = useSelector((state: State) => state.boards);
  const partnerRepresentants = useSelector(
    (state: State) => state.partnerRepresentants
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [filteredBoards, setFilteredBoards] = useState<Board[]>([]);

  const fetchData = useCallback(async () => {
    if (!societyId) {
      return;
    }

    try {
      setIsLoading(true);
      setIsError(false);
      await Promise.all([
        dispatch(getBoards(societyId)),
        dispatch(getPartnerRepresentants({ societyId })),
      ]);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [societyId, dispatch]);

  const filterBoards = useCallback(
    (boardsToFilter: Board[]) => {
      if (!boardsToFilter.length || !memberId) {
        setFilteredBoards([]);
        return;
      }

      const visibleBoards = boardsToFilter
        .filter((board) => isBoardVisibleAtList(board, memberId, user))
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );

      setFilteredBoards(visibleBoards);
    },
    [memberId, user]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    filterBoards(boards);
  }, [boards, filterBoards]);

  return {
    data: filteredBoards,
    partnerRepresentants,
    isLoading,
    isError,
  };
};
